/**
 * AudioEngine
 *
 * Owns the AudioContext, the voice bus, and the master output. Nothing in the
 * app should create an AudioContext directly; everything goes through here so
 * we have one place to handle user-gesture unlock, sample-rate changes, tab
 * suspend/resume, AudioWorklet loading, and teardown.
 *
 * Audio graph at rest (no effects):
 *
 *   [voiceBus]  →  [master]  →  [safetyLimiter]  →  ctx.destination
 *
 * With an effects chain wired (Phase 5):
 *
 *   [voiceBus] → [dist] → [delay] → [bitcrusher] → [master] → [safetyLimiter] → ctx.destination
 *
 * Voices always connect to `voiceBus`. Effect modules insert themselves
 * between `voiceBus` and `master`. `master` holds the user-controlled output
 * volume. `safetyLimiter` is a hard brick-wall compressor between master and
 * the device — it catches runaway gain from feedback loops, extreme mod
 * connections, or randomized patches that hit a resonant peak, so the user's
 * speakers/ears stay protected no matter what the patch does.
 */

export type EngineState = 'closed' | 'suspended' | 'running';

/**
 * Listener fired when the operating system's audio devices change —
 * a Bluetooth headset disconnects, an external interface is plugged
 * in, the user switches output in their OS settings. The browser
 * doesn't auto-migrate an active AudioContext to the new device, so
 * this is mostly informational; the UI can warn the user that the
 * sample rate they're hearing might not match the new device's
 * preferred rate without a page refresh.
 */
export type DeviceChangeListener = () => void;

export interface EngineOptions {
  /** Master output gain (0–1). Default 0.5 — loud patches come soon. */
  masterGain?: number;
}

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private voiceBusNode: GainNode | null = null;
  private masterNode: GainNode | null = null;
  private safetyLimiterNode: DynamicsCompressorNode | null = null;
  private readonly options: Required<EngineOptions>;
  private readonly listeners = new Set<(state: EngineState) => void>();
  private readonly loadedWorklets = new Set<string>();

  constructor(options: EngineOptions = {}) {
    this.options = {
      masterGain: options.masterGain ?? 0.5,
    };
  }

  /**
   * Build the AudioContext + voice bus + master without resuming.
   * Safe to call before any user gesture — the context starts in
   * `'suspended'` state so audio won't actually flow until
   * `unlock()` runs, but the entire audio graph (modules, effects,
   * voices) can be constructed and wired up immediately. Idempotent.
   *
   * Why split this from `unlock()`? Browsers require a user gesture
   * to *resume* an AudioContext, but creating one and wiring nodes
   * is fine to do on page load. Splitting lets the rack render its
   * default effects + knobs the moment the user lands on the page;
   * we only need a gesture to actually start producing sound.
   */
  prepare(): void {
    if (this.ctx) return;
    const Ctor: typeof AudioContext =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctor) {
      throw new Error('Web Audio API is not supported in this browser.');
    }
    this.ctx = new Ctor({ latencyHint: 'interactive' });

    this.voiceBusNode = this.ctx.createGain();
    this.voiceBusNode.gain.value = 1;

    this.masterNode = this.ctx.createGain();
    this.masterNode.gain.value = this.options.masterGain;

    // Safety limiter — last stage before the device. Brick-wall
    // ratio + low threshold + fast attack means a runaway signal
    // (randomizer hitting a resonant peak, feedback loop, env-mod
    // pinning a filter cutoff) gets clamped instead of melting
    // headphones. Tuned to be transparent on normal patches and
    // aggressive on hot ones.
    this.safetyLimiterNode = this.ctx.createDynamicsCompressor();
    this.safetyLimiterNode.threshold.value = -3;
    this.safetyLimiterNode.knee.value = 0;
    this.safetyLimiterNode.ratio.value = 20;
    this.safetyLimiterNode.attack.value = 0.001;
    this.safetyLimiterNode.release.value = 0.05;

    // Default: voice bus feeds master directly. FX chains break and
    // remake this edge.
    this.voiceBusNode.connect(this.masterNode);
    this.masterNode.connect(this.safetyLimiterNode);
    this.safetyLimiterNode.connect(this.ctx.destination);

    this.ctx.addEventListener('statechange', () => this.notify());
    this.notify();
  }

  /**
   * Ensure the AudioContext exists AND is running. Calls `prepare()`
   * implicitly so it can run as a one-stop "unlock me" entry point;
   * the resume step still requires being called from a user gesture
   * handler — click, keydown, etc. Safe to call repeatedly.
   */
  async unlock(): Promise<void> {
    this.prepare();
    if (this.ctx!.state === 'suspended') {
      await this.ctx!.resume();
    }
    this.notify();
  }

  /**
   * Load an AudioWorklet module. Idempotent — subsequent calls with the same
   * url are a no-op. Must be called after unlock().
   */
  async loadWorklet(url: string): Promise<void> {
    if (!this.ctx) {
      throw new Error('AudioEngine not unlocked — call unlock() first.');
    }
    if (this.loadedWorklets.has(url)) return;
    await this.ctx.audioWorklet.addModule(url);
    this.loadedWorklets.add(url);
  }

  /** Current state of the underlying AudioContext, or 'closed' if not created. */
  get state(): EngineState {
    if (!this.ctx) return 'closed';
    return this.ctx.state as EngineState;
  }

  /** The live AudioContext. Throws if called before unlock(). */
  get context(): AudioContext {
    if (!this.ctx) {
      throw new Error('AudioEngine not unlocked yet — call unlock() first.');
    }
    return this.ctx;
  }

  /** The voice bus — every Voice connects its output here. */
  get voiceBus(): GainNode {
    if (!this.voiceBusNode) {
      throw new Error('AudioEngine not unlocked yet — call unlock() first.');
    }
    return this.voiceBusNode;
  }

  /** The master output — the last stage before ctx.destination. */
  get master(): GainNode {
    if (!this.masterNode) {
      throw new Error('AudioEngine not unlocked yet — call unlock() first.');
    }
    return this.masterNode;
  }

  /**
   * @deprecated Prefer `voiceBus` (for Voice connections) or `master` (for
   * the final stage). Kept so existing code paths still compile.
   */
  get output(): GainNode {
    return this.voiceBus;
  }

  /**
   * Wire visibility / focus listeners that try to resume a suspended
   * context when the user returns to the tab. Some browsers (notably
   * iOS Safari) suspend AudioContexts aggressively in the background
   * and don't auto-resume on visibility — the synth would silently
   * stay quiet without this nudge. Idempotent: safe to call multiple
   * times. No-op when the context isn't created yet.
   */
  attachLifecycleHandlers(): void {
    if (this.lifecycleAttached) return;
    this.lifecycleAttached = true;

    const tryResume = (): void => {
      if (!this.ctx) return;
      // Only attempt resume if the user has already unlocked once
      // (master gain has been wired). Pre-unlock the gesture
      // requirement still applies.
      if (!this.masterNode) return;
      if (this.ctx.state === 'suspended') {
        // The promise is best-effort — browsers silently reject when
        // resume is called outside a gesture. We catch and move on.
        this.ctx.resume().catch(() => {
          /* will retry on next visibility change or user gesture */
        });
      }
    };

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') tryResume();
    });
    window.addEventListener('focus', tryResume);

    // Audio output device change — Bluetooth disconnect, plug-in, OS
    // output swap. The browser keeps the AudioContext bound to the
    // device that was current at construction time, so a hot-swap
    // can leave the synth silent or playing through the wrong speaker
    // until the user refreshes. We can't migrate the context — there's
    // no Web Audio API for that — but we CAN notify subscribers so
    // they can surface a toast inviting the user to refresh.
    if (
      typeof navigator !== 'undefined' &&
      navigator.mediaDevices?.addEventListener
    ) {
      navigator.mediaDevices.addEventListener('devicechange', () => {
        for (const fn of this.deviceChangeListeners) fn();
      });
    }
  }
  private lifecycleAttached = false;

  /**
   * Subscribe to "audio devices changed" notifications. Fires on
   * `navigator.mediaDevices.devicechange` once `attachLifecycleHandlers`
   * has been called. Returns an unsubscribe function.
   */
  onDeviceChange(listener: DeviceChangeListener): () => void {
    this.deviceChangeListeners.add(listener);
    return () => {
      this.deviceChangeListeners.delete(listener);
    };
  }
  private readonly deviceChangeListeners = new Set<DeviceChangeListener>();

  /**
   * Sample rate the AudioContext was constructed at. Pre-unlock returns
   * `null` since no context exists yet. After the first device change
   * the user might prefer a different rate, but we can't offer it
   * without a full re-init — exposing this lets the UI surface it for
   * comparison ("you're hearing 44.1k; new device prefers 48k, refresh
   * to switch?").
   */
  get sampleRate(): number | null {
    return this.ctx?.sampleRate ?? null;
  }

  /**
   * Play a short sine test tone. Phase 0 smoke test — proves the audio path
   * and the gesture-unlock flow are wired correctly.
   */
  async testTone(frequency = 440, durationMs = 600): Promise<void> {
    await this.unlock();
    const ctx = this.context;
    const now = ctx.currentTime;
    const attack = 0.01;
    const release = 0.08;
    const holdSec = Math.max(0, durationMs / 1000 - attack - release);

    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = frequency;

    const env = ctx.createGain();
    env.gain.setValueAtTime(0, now);
    env.gain.linearRampToValueAtTime(0.35, now + attack);
    env.gain.setValueAtTime(0.35, now + attack + holdSec);
    env.gain.linearRampToValueAtTime(0, now + attack + holdSec + release);

    osc.connect(env).connect(this.voiceBus);

    osc.start(now);
    osc.stop(now + attack + holdSec + release + 0.02);
    osc.onended = () => {
      osc.disconnect();
      env.disconnect();
    };
  }

  /** Subscribe to engine state changes. Returns an unsubscribe function. */
  onStateChange(listener: (state: EngineState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    const state = this.state;
    for (const listener of this.listeners) listener(state);
  }
}
